// FAILURE-STOP: Continue on a run the server stopped after repeated failures
// (`POST runs/{id}/failures/continue`). A live run's error counts and stop are cleared and its
// agent's tools answer again; a stopped run that has ended wakes its DE on the task again. One
// button for the inbox item and the run page; the server decides who may (who may view the run).
import { Button } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlayIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { RunRow } from "../../api/types";
import { problemText } from "../de/de-status";
import { invalidateDecided } from "./agent-queries";

/**
 * The inbox line of a failure stop (failure-stop.ts `stopSummary`), or of a loop stop
 * (loop-breaker/detect.ts `stopReason`: the runtime's guard, a repeat or a stall). QA-FIXES-2: a
 * loop-stopped run is continued the same way (the API wakes its task or message again).
 */
export const FAILURE_STOP_LINE =
  /stopped after repeated failures|^Stopped: (?:repeating|no progress)/i;

/** The run was stopped after repeated failures (its `failure_guard.stopped`). */
export function failureStopped(run: { failure_guard?: RunRow["failure_guard"] }): boolean {
  return Boolean(run.failure_guard?.stopped);
}

export function ContinueRunButton({
  api,
  co,
  runId,
  size = "default",
  onContinued,
}: {
  api: Api;
  co: string;
  runId: string;
  size?: "xs" | "sm" | "default" | "lg";
  onContinued?: (run: RunRow) => void;
}) {
  const qc = useQueryClient();
  const go = useMutation({
    mutationFn: () => api.client.runs.continueAfterFailures({ params: { co, id: runId } }),
    onSuccess: (run) => onContinued?.(run as RunRow),
    onSettled: () => invalidateDecided(qc, co),
  });
  return (
    <span className="inline-flex items-center gap-2">
      <Button
        size={size}
        disabled={go.isPending}
        onClick={() => go.mutate()}
        data-slot="continue-run"
      >
        <PlayIcon aria-hidden="true" />
        {go.isPending ? "Continuing…" : "Continue"}
      </Button>
      {go.isError ? (
        <span role="alert" className="text-xs text-destructive">
          {problemText(go.error, "The run could not be continued.")}
        </span>
      ) : null}
    </span>
  );
}
